declare const __IS_PRODUCTION__: boolean;

import { useLocation, useSearchParams } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import styled, { css } from 'styled-components';
import { IframeApp } from '~/shared/types';
import IframeResizer, { IFrameObject } from 'iframe-resizer-react';

import getAppParams from '~/widget/utils/getAppParams';
import useWidgetContext from '~/widget/hooks/useWidgetContext';
import useFrameSizeSelector from '~/widget/hooks/useFrameSizeSelector';
import formatContextForContentWindow from '~/widget/utils/formatContextForContentWindow';
import IngestParamsContext from '~/widget/context/IngestParamsContext';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  consentSelector,
  loadingSelector,
  widgetState,
} from '~/widget/state/widgetState';
import useDebouncedCallback from '~/widget/hooks/useDebouncedCallback';
import getUserId from '~/widget/utils/getUserId';
import getWidgetMode from '~/shared/utils/getWidgetMode';

export type Props = {
  dataTestId?: string;
  app: IframeApp;
  minHeight: number;
};

export const APP_FRAME_ID = 'app_frame';

/**
 * Difference between BasicFrame and Frame
 *
 * BasicFrame
 * - Plain HTML iframe (no auto-resize)
 * - Height is fixed except for the given minHeight; the app scrolls itself
 * - Used when app.bypassResizer === true
 *
 * Frame
 * - iframe-resizer is enabled (auto height based on content)
 * - Supports in-page links and sets body margin/padding for child
 * - Default when app.bypassResizer !== true
 */

const FrameFacade: React.FC<Props> = ({ app, minHeight }) => {
  const setLoading = useSetRecoilState(loadingSelector);
  const { locale, loading } = useRecoilValue(widgetState);
  const [timesEmitted, setTimesEmitted] = useState(0);
  // The user's consent settings. We send this to the app in the iframe.
  const consentParams = useRecoilValue(consentSelector);

  const context = useWidgetContext();
  const ingestParams = useContext(IngestParamsContext);
  const currentBp = useFrameSizeSelector();

  const location = useLocation();

  const [searchParams] = useSearchParams();
  const queryParams = searchParams.get('queryParams');
  const iframeRef = useRef<IFrameObject | null>(null);

  const appParams = getAppParams(
    {
      locale: locale,
      hostPageParams: context.hostPageParams,
      // If query params isn't a string we cannot join them easily so we'll just skip them
      queryParams: typeof queryParams === 'string' ? queryParams : undefined,
    },
    app,
  );
  const bodyMargin = app.styleParams
    ? (app.styleParams[currentBp]?.bodyMargin ?? null)
    : null;
  const bodyPadding = app.styleParams
    ? (app.styleParams[currentBp]?.bodyPadding ?? null)
    : null;

  // When the partner uses query params we need to append to these params and not create invalid params with multiple `?`
  const { frameSource, trackSetupPayload } = useMemo(() => {
    const frameSource = app.appUrl.includes('?')
      ? `${app.appUrl}&${appParams}`
      : `${app.appUrl}?${appParams}`;

    const appUrlObject = new URL(frameSource);

    const widgetMode = getWidgetMode(location.pathname);
    const widgetType: 'sap' | 'wgt' =
      widgetMode === 'standalone' ? 'sap' : 'wgt';
    const trackSetupPayload = {
      bndl_user_id: getUserId(),
      bndl_account_id: context.accountId,
      bndl_widget_type: widgetType,
    };

    appUrlObject.searchParams.set(
      'bndl_wgt_setup',
      JSON.stringify(trackSetupPayload),
    );

    if (context.bntrk) {
      appUrlObject.searchParams.set('bntrk', context.bntrk);
    }

    return {
      frameSource: appUrlObject.toString(),
      trackSetupPayload,
      widgetType,
    };
  }, [app.appUrl, appParams]);

  useEffect(() => {
    const iframeElement = document.getElementById(
      APP_FRAME_ID,
    ) as HTMLIFrameElement | null;

    if (
      iframeElement !== null &&
      iframeElement.contentWindow &&
      loading === false
    ) {
      const emitContext = formatContextForContentWindow(context);

      iframeElement.contentWindow?.postMessage(
        JSON.stringify({
          type: 'emitWidgetContext',
          payload: emitContext,
        }),
        '*',
      );

      // Send consent to the iframe app.
      // It's being used to enable or disable trackers.
      // Runs on first load and after in-iframe navigation.
      iframeElement.contentWindow?.postMessage(
        JSON.stringify({
          type: 'receiveConsent',
          payload: consentParams,
        }),
        '*',
      );

      iframeElement.contentWindow?.postMessage(
        JSON.stringify({
          type: 'ingestQueryParams',
          payload: ingestParams?.bndlData?.data ?? {},
        }),
        '*',
      );

      iframeElement.contentWindow?.postMessage(
        JSON.stringify({
          type: 'bndl_track_setup',
          payload: trackSetupPayload,
        }),
        '*',
      );

      const innerContainer = document.getElementById('inner-container');
      if (innerContainer) {
        // Send iframe dimensions to the app
        const iframeRect = innerContainer.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(innerContainer);
        const isVisible =
          computedStyle.display !== 'none' &&
          computedStyle.visibility !== 'hidden';

        const dimensions = {
          width: Math.round(iframeRect.width),
          height: Math.round(iframeRect.height),
          visible: isVisible,
        };

        iframeElement.contentWindow?.postMessage(
          JSON.stringify({
            type: 'iframeDimensions',
            payload: dimensions,
          }),
          '*',
        );
      }
    }
    // Re-run when the iframe navigates (timesEmitted changes) or consent changes
  }, [loading, context, timesEmitted, consentParams]);
  const emitOnLoad = () => {
    // When loading is false and this callback fires again
    // that means that we've navigated from inside the app.
    // This could land on in a new app when e.g. iFrameBridge('openApp') was used.
    // When that happens we need to emit the widget context again,
    // so that the next app's iFrameBridge can also be initialized.
    if (loading === false) {
      setTimesEmitted((prev) => prev + 1);
    }
    setLoading(false);
  };

  // Send container dimensions on resize
  const sendContainerDimensions = useDebouncedCallback(
    (containerElement: HTMLElement) => {
      const rect = containerElement.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(containerElement);
      const isVisible =
        computedStyle.display !== 'none' &&
        computedStyle.visibility !== 'hidden';

      // Send to any iframe content windows
      const iframeElement = document.getElementById(
        APP_FRAME_ID,
      ) as HTMLIFrameElement | null;

      iframeElement?.contentWindow?.postMessage(
        JSON.stringify({
          type: 'iframeDimensions',
          payload: {
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            visible: isVisible,
          },
        }),
        '*',
      );
    },
    250,
  );

  // Set up resize observer for the inner container
  useEffect(() => {
    const innerContainer = document.getElementById('inner-container');
    if (!innerContainer) return;

    const resizeObserver = new ResizeObserver((_entries) => {
      sendContainerDimensions(innerContainer);
    });

    resizeObserver.observe(innerContainer);

    // Send initial dimensions
    sendContainerDimensions(innerContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [sendContainerDimensions]);

  /**
   * Here we return a basic iFrame without iFrame resizer
   */
  if (app.bypassResizer === true) {
    return (
      <BasicFrame
        id={APP_FRAME_ID}
        src={frameSource}
        onLoad={emitOnLoad}
        style={{
          minHeight: minHeight + 'px',
        }}
        /**
         * We removed lazy loading because it was incompatible with certain apps
         * e.g.: "kidwk" of hypotheekbond.
         *
         * https://discord.com/channels/778915897027854377/1375058224805445674
         */
        // loading="lazy"
      />
    );
  }

  return (
    <Frame
      forwardRef={iframeRef}
      id={APP_FRAME_ID}
      inPageLinks
      checkOrigin={false}
      heightCalculationMethod="bodyOffset"
      resizeFrom="child"
      src={frameSource}
      sizeWidth={false}
      bodyMargin={bodyMargin}
      bodyPadding={bodyPadding}
      onLoad={emitOnLoad}
      allow={
        __IS_PRODUCTION__
          ? 'clipboard-write'
          : 'clipboard-write; private-network-access'
      }
      style={{
        // minHeight: minHeight + 'px',
        /**
         * Indicates that the element can be rendered using the page's color scheme settings.
         * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/color-scheme#normal
         */
        colorScheme: 'normal',
      }}
      /**
       * @see emitOnLoad function
       */
      // loading="lazy"
    />
  );
};

const Frame = styled(IframeResizer)(
  () => css`
    height: 100%;
    border: none;
    width: 100%;

    transition: opacity 0.3s ease-out;
  `,
);

const BasicFrame = styled.iframe(
  () => css`
    /* Without subtracting 3px we render a scroll bar. Since we're assuming this type of app
     * will deal with their own scroll bar we need to disable this all together.
     */
    height: calc(100% - 3px);
    border: none;
    width: 100%;

    transition: opacity 0.3s ease-out;
  `,
);

export default FrameFacade;
